
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { passwordChecks } from '@/utils/form-validator';
import PasswordRequirement, {
  RequirementStatus,
} from './PasswordRequirement.vue';

type OptionalCheck = 'digit' | 'lowercase' | 'uppercase' | 'special';

@Component({
  components: {
    'ck-password-requirement': PasswordRequirement,
  },
})
export default class PasswordRequirements extends Vue {
  @Prop({ required: true }) password!: string;
  @Prop({ required: true }) specialCharsLabel!: string;
  @Prop({ default: false }) blurred!: boolean;

  showSpecialChars = false;

  private lengthEverMet = false;
  private optionalEverSatisfied = false;

  get checks() {
    return {
      length: passwordChecks.hasValidLength(this.password),
      digit: passwordChecks.hasDigit(this.password),
      lowercase: passwordChecks.hasLowercase(this.password),
      uppercase: passwordChecks.hasUppercase(this.password),
      special: passwordChecks.hasSpecialChar(this.password),
    };
  }

  get optionalMetCount(): number {
    const { digit, lowercase, uppercase, special } = this.checks;
    return [digit, lowercase, uppercase, special].filter(Boolean).length;
  }

  get lengthStatus(): RequirementStatus {
    if (this.checks.length) {
      return 'met';
    }
    if (this.lengthEverMet) {
      return 'failed';
    }
    if (this.blurred) {
      return 'failed';
    }
    return 'pristine';
  }

  get lengthLabel(): string {
    const base = this.$t('register.password-requirement-1') as string;

    if (this.lengthStatus === 'failed') {
      if (this.password.length > 16) {
        return `${base} (${this.$t('register.password-too-long')})`;
      }
      if (this.password.length < 8 && this.password.length > 0) {
        return `${base} (${this.$t('register.password-too-short')})`;
      }
    }

    return base;
  }

  get optionalStatus(): RequirementStatus {
    if (this.optionalMetCount >= 3) {
      return 'met';
    }
    if (this.optionalEverSatisfied) {
      return 'failed';
    }
    if (this.blurred) {
      return 'failed';
    }
    return 'pristine';
  }

  get counterClass(): string {
    switch (this.optionalStatus) {
      case 'met':
        return 'counter--success';
      case 'failed':
        return 'counter--warning';
      default:
        return this.optionalMetCount >= 2
          ? 'counter--warning'
          : 'counter--default';
    }
  }

  @Watch('checks', { deep: true, immediate: true })
  onChecksChange(): void {
    if (this.checks.length) {
      this.lengthEverMet = true;
    }
    if (this.optionalMetCount >= 3) {
      this.optionalEverSatisfied = true;
    }
  }

  getOptionalStatus(key: OptionalCheck): RequirementStatus {
    const currentlyMet = this.checks[key];

    if (currentlyMet) {
      return 'met';
    }

    if (this.optionalMetCount >= 3) {
      return 'pristine';
    }

    if (this.optionalEverSatisfied && this.optionalMetCount < 3) {
      return 'failed';
    }

    if (this.blurred && this.optionalMetCount < 3) {
      return 'failed';
    }

    return 'pristine';
  }
}
